import { faExclamationCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DARK_GRAY, LOW_GRAY, RED, translate } from "@shape/reef-shared";
import { PropsWithChildren, useState } from "react";
import {
  get,
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldError as FieldErrorType,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
  UseFormStateReturn,
} from "react-hook-form";
import styled from "styled-components";

const Container = styled.div`
  width: inherit;
  animation: fadeIn 0.2s ease;
  word-break: break-word;

  > h5 {
    margin: 0;
    margin-bottom: 8px;

    & > div {
      margin-left: 6px;
      position: relative;
      display: inline-flex;
      height: 16px;
      padding-right: 4px;
      top: 2px;
    }
  }

  p {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    display: flex;
    align-items: center;
    color: ${LOW_GRAY};
  }
`;

const Info = styled.div`
  position: absolute;
  width: 220px;
  min-height: 32px;
  top: -16px;
  left: 28px;
  padding: 6px;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: normal;
  background-color: ${DARK_GRAY};
  border-radius: 5px;
  transform-origin: left center;
  animation: showInfo 0.1s ease-out;

  &:before {
    content: "";
    position: absolute;
    width: 0px;
    height: 0px;
    left: -10px;
    top: 11px;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid ${DARK_GRAY};
  }

  @keyframes showInfo {
    0% {
      transform: scale(0.5);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const FieldContainer = styled.div<{ fieldClassName?: string }>`
  min-height: ${(props) => (props.fieldClassName === "switch-field" ? "unset" : "36px")};
  font-size: 14px;
  display: flex;
  align-items: center;

  & > div:has(textarea) {
    width: 100%;
  }
`;

export const FieldError = styled.div`
  font-size: 13px;
  color: ${RED};
  margin-top: 4px;

  svg {
    margin-right: 4px;
  }
`;

export interface BaseFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> {
  name: TName;
  label?: string;
  subtitle?: string;
  defaultValue?:
    | TFieldValues[TName]
    | string
    | number
    | boolean
    | Partial<Record<string, any>>[]
    | Partial<Record<string, any>>
    | null;
  rules?: Omit<RegisterOptions<TFieldValues, TName>, "valueAsNumber" | "valueAsDate" | "disabled">;
  form: UseFormReturn<TFieldValues>;
  fieldClassName?: string;
  readOnly?: boolean;
  labelInfo?: string;
}

export interface BaseFieldPropsWithRender<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }) => JSX.Element;
}

export function BaseField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  label,
  subtitle,
  defaultValue,
  rules,
  render,
  form,
  fieldClassName,
  labelInfo,
  readOnly,
}: PropsWithChildren<BaseFieldPropsWithRender<TFieldValues, TName>>) {
  const error = get(form.formState.errors, name) as FieldErrorType | undefined;
  const hasError = Boolean(error?.type);
  const [showLabelInfo, setShowLabelInfo] = useState(false);
  const isRequired = rules?.required;
  const isValidate = rules?.validate;

  return (
    <Container className={`field ${fieldClassName ?? ""}`}>
      {label ? (
        <h5>
          {label} {(isRequired || isValidate) && " *"}
          {labelInfo ? (
            <div
              role="tooltip"
              onMouseEnter={() => setShowLabelInfo(true)}
              onMouseLeave={() => setShowLabelInfo(false)}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
              {showLabelInfo ? <Info>{labelInfo}</Info> : null}
            </div>
          ) : null}
        </h5>
      ) : null}
      {subtitle && <p>{subtitle}</p>}
      <FieldContainer fieldClassName={fieldClassName}>
        <Controller
          name={name}
          control={form.control as Control<TFieldValues>}
          defaultValue={(readOnly && !defaultValue ? "-" : defaultValue) as TFieldValues[TName]}
          rules={rules}
          render={({ field, fieldState, formState }) => (
            <div style={{ width: "100%" }} className={fieldState.error ? "error" : ""}>
              {render({ field, fieldState, formState })}
            </div>
          )}
        />
      </FieldContainer>
      {hasError ? (
        <FieldError className="hide-error">
          <FontAwesomeIcon icon={faExclamationCircle} size="1x" />
          <span>{error?.message || translate("form.thisField")}</span>
        </FieldError>
      ) : null}
    </Container>
  );
}
